import { useCallback, useEffect, useRef, useState } from "react";
import AutoCompleteClient from "/Helpers/AutoCompleteClient";
import {
  ReactSelectItem,
  stringsToReactSelectItems,
} from "/Helpers/ReactSelectItem";

/**
 * This hook fetches autocomplete suggestions for search fields. Each new term cancels the
 * request for the previous one, so a slow response can't replace newer suggestions. An empty
 * term, missing configuration or a failed request clears the suggestions.
 */
export default (endpoint?: string, language?: string) => {
  const [items, setItems] = useState<ReactSelectItem[]>([]);
  const pendingRequest = useRef<AbortController | null>(null);

  const fetchSuggestions = useCallback(
    (term: string) => {
      pendingRequest.current?.abort();
      if (!term || !endpoint || !language) {
        setItems([]);
        return;
      }
      const request = new AbortController();
      pendingRequest.current = request;
      new AutoCompleteClient(endpoint, language)
        .GetItems(term, request.signal)
        .then((res) => {
          if (!request.signal.aborted) setItems(stringsToReactSelectItems(res));
        })
        .catch(() => {
          if (!request.signal.aborted) setItems([]);
        });
    },
    [endpoint, language],
  );

  useEffect(() => () => pendingRequest.current?.abort(), [endpoint, language]);

  return { items, setItems, fetchSuggestions };
};
